import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import server from '../../global/server';
import { useApp } from '../../context/AppContext';
import LoadingButton from '@mui/lab/LoadingButton';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import useHandleErrors from '../../hooks/useHandleErrors';
import {
    Alert,
    Collapse,
    Divider,
    Avatar,
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useFetch } from '../../hooks/useFetch';
import LoadingContent, { ErrorContent } from '../../components/LoadingContent';
import {
    isAfter,
    parseISO,
} from 'date-fns';
import RaffleWheel from './components/RaffleWheel';
import RaffleSettings from './components/RaffleSettings';
import {
    VideoCall,
    Edit,
    ArrowBack,
    AccessTime,
    PersonOutline,
    DeleteOutline,
    CalendarToday,
    Casino as CasinoIcon,
    PeopleOutline,
    Close,
    EmojiEvents,
    ContentCopy,
    ExpandMore,
    Add,
    Settings as SettingsIcon,
} from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';
import { getServerNow, useServerTime } from '../../hooks/useServerTime';
import BotaoConversar from '../../components/BotaoConversar';

const SectionCard = ({ children, sx = {} }) => (
    <Box sx={{
        width: '100%',
        bgcolor: 'white',
        borderRadius: '16px',
        border: '1px solid #e8e8e8',
        p: { xs: '16px', sm: '20px 24px' },
        ...sx,
    }}>
        {children}
    </Box>
);

// Detalhes do conviva que adquiriu o horario, abertos pela setinha do card
const DetalhesConviva = ({ buyer }) => {
    const [aberto, setAberto] = useState(false);

    const naoInformado = valor => (valor && String(valor).trim() ? valor : '(Não informado)');

    const linhas = [
        ['Usuário', buyer.username ? `@${buyer.username}` : '(Não informado)'],
        // Sem e-mail, telefone ou Instagram: o item 1.11 dos Termos veda a
        // Empiria repassar contato pessoal e direto de um Usuario a outro,
        // e perfil em rede social esta na lista.
        ['Profissão', naoInformado(buyer.profession)],
        ['Formação', naoInformado(buyer.education)],
        ['Residência', naoInformado(buyer.residence)],
        ['Sobre', naoInformado(buyer.description)],
    ];

    return (
        <>
            <Stack direction="row" justifyContent="flex-end">
                <IconButton
                    size="small"
                    onClick={e => {
                        e.stopPropagation();
                        setAberto(atual => !atual);
                    }}
                    sx={{
                        color: '#888',
                        transform: aberto ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s',
                    }}
                >
                    <ExpandMore sx={{ fontSize: 20 }} />
                </IconButton>
            </Stack>

            <Collapse in={aberto} unmountOnExit>
                <Divider sx={{ mb: 1 }} />
                <Stack spacing={0.75}>
                    {linhas.map(([rotulo, valor]) => (
                        <Stack key={rotulo} direction="row" spacing={1.5} alignItems="flex-start">
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, minWidth: 96, flexShrink: 0 }}>
                                {rotulo}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                {valor}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </Collapse>
        </>
    );
};

export default () => {
    const { user } = useAuth();
    const [raffleOpen, setRaffleOpen] = useState(false);
    const [raffleData, setRaffleData] = useState(null);
    const [participantsDialog, setParticipantsDialog] = useState(null);
    const [raffleSettings, setRaffleSettings] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const { isOffline } = useApp();
    useServerTime();
    const [hasError, setError] = useState();
    useHandleErrors(hasError);
    const params = useParams();
    const navigation = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const confirm = useConfirm();

    const { data, error, mutate, isLoading } = useFetch(`/profile/meeting?id=${params.id}`);
    const meeting = Array.isArray(data) ? data[0] : data;
    useHandleErrors(error);

    const removeSchedule = async (schedule_id) => {
        try {
            await confirm({ title: 'Remover horário?', description: 'Esta ação é irreversível.' });
        } catch { return; }
        try {
            setDeleting(true);
            await server.delete(`/profile/meeting/schedule/${schedule_id}`);
            await mutate();
            enqueueSnackbar('Horário removido', { variant: 'success' });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Erro ao remover', { variant: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    const BackToCalendar = () => (
        <Box sx={{ width: '100%', maxWidth: 720, mx: 'auto', px: { xs: '12px', sm: '16px' }, pt: 2 }}>
            <Button startIcon={<ArrowBack />} onClick={() => navigation(-1)} sx={{ textTransform: 'none', color: '#666' }}>
                Voltar
            </Button>
        </Box>
    );

    if (isLoading) return <><BackToCalendar /><LoadingContent /></>;
    if (error) return <><BackToCalendar /><ErrorContent label={error?.response?.data?.message} /></>;

    const allSchedules = (meeting?.meeting_schedule || [])
        .filter(s => s.datetime)
        .sort((a, b) => new Date(a.datetime) - new Date(b.datetime));

    const getScheduleStatus = (item) => {
        const now = getServerNow();
        const meetingTime = parseISO(item.datetime);
        const diff = (now.getTime() - meetingTime.getTime()) / 1000;
        const orderCount = Array.isArray(item.Order) ? item.Order.length : 0;
        // Finalizado nunca volta a ficar "ao vivo", nem dentro da hora do encontro
        if (item.canceled_at) return 'canceled';
        if (item.finished_at) return 'ended';
        if (diff >= 0 && diff <= 3600 && orderCount > 0) return 'live';
        if (diff > 3600) return 'ended';
        return 'upcoming';
    };

    // Separar futuros/ao vivo e passados
    const now = getServerNow();
    const upcoming = allSchedules.filter(s => {
        const status = getScheduleStatus(s);
        return status === 'upcoming' || status === 'live';
    });
    const ended = allSchedules.filter(s => ['ended', 'canceled'].includes(getScheduleStatus(s)));

    const formatarMomento = data =>
        data ? new Date(data).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : null;

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: 720,
            mx: 'auto',
            p: { xs: '16px 12px 32px', sm: '24px 16px 48px' },
            gap: 2.5,
        }}>
            {/* Header */}
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button startIcon={<ArrowBack />} onClick={() => navigation(-1)} sx={{ textTransform: 'none', color: '#666' }}>
                    Voltar
                </Button>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    {/* Encontro encerrado nao se edita: dele so sai uma recriacao */}
                    {upcoming.length > 0 && (
                        <Button
                            startIcon={<Edit />}
                            onClick={() => navigation(`/meus-encontros/${params.id}/detalhes`)}
                            sx={{ textTransform: 'none', color: '#006a61' }}
                        >
                            Editar
                        </Button>
                    )}
                    {upcoming.length === 0 && (
                        <Tooltip title="Recriar encontro" arrow>
                            <IconButton
                                onClick={() => navigation(`/meus-encontros/novo?recriar=${params.id}`)}
                                sx={{ color: '#2e7d32', '&:hover': { bgcolor: 'transparent', color: '#1b5e20' } }}
                            >
                                <Add sx={{ fontSize: 22 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Stack>
            </Box>

            <Box sx={{ textAlign: 'center', width: '100%' }}>
                <Typography variant="h6" fontWeight={600}>Acompanhar encontro</Typography>
                <Typography variant="body2" color="text.secondary">"{meeting?.title}"</Typography>
                {meeting?.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 560, mx: 'auto' }}>
                        {meeting.description}
                    </Typography>
                )}
            </Box>

            {meeting?.raffle && (
                <Alert severity="info" sx={{ width: '100%', borderRadius: '12px', '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                    Este encontro é na modalidade <strong>sorteio</strong>. Realize o sorteio manualmente a qualquer momento.
                    Caso não seja realizado, o sistema sorteará automaticamente no prazo definido na
                    engrenagem do horário — ou <strong>10 minutos antes</strong> do horário marcado, se nenhum prazo for definido.
                </Alert>
            )}

            {/* Proximos / Ao vivo */}
            {upcoming.length > 0 && (
                <>
                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ alignSelf: 'flex-start', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem' }}>
                        Próximos ({upcoming.length})
                    </Typography>
                    <Stack spacing={1.5} sx={{ width: '100%' }}>
                        {upcoming.map(item => {
                            const orders = Array.isArray(item.Order) ? item.Order : [];
                            const orderCount = orders.length;
                            const chosenOrder = orders.find(o => o.chosen);
                            const raffleCompleted = meeting?.raffle && chosenOrder;
                            const buyer = raffleCompleted ? chosenOrder.user : (!meeting?.raffle ? orders[0]?.user : null);
                            const status = getScheduleStatus(item);
                            const dt = new Date(item.datetime);
                            const date = dt.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
                            const time = dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

                            return (
                                <SectionCard key={item.id} sx={{
                                    borderLeft: `4px solid ${status === 'live' ? '#2e7d32' : orderCount > 0 ? '#006a61' : '#ddd'}`,
                                }}>
                                    <Stack spacing={1.5}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CalendarToday sx={{ fontSize: 16, color: '#888' }} />
                                                <Typography variant="subtitle2" fontWeight={600}>
                                                    {date}
                                                </Typography>
                                                <AccessTime sx={{ fontSize: 16, color: '#888' }} />
                                                <Typography variant="subtitle2" fontWeight={600}>
                                                    {time}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                {/* Prazo do sorteio automatico: so faz sentido
                                                    enquanto ninguem foi sorteado */}
                                                {meeting?.raffle && !raffleCompleted && (
                                                    <Tooltip title="Configurações de sorteio" arrow>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => setRaffleSettings(item)}
                                                            sx={{ color: item.raffle_at ? '#7b1fa2' : '#999' }}
                                                        >
                                                            <SettingsIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {/* Cancelar e trocar o horario moram na tela de edicao do
                                                    encontro (/meus-encontros/:id/detalhes): e la que o
                                                    anfitriao espera mexer no encontro. Aqui so se acompanha. */}
                                            {status === 'live' && (
                                                <Chip label="Ao vivo" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem' }} />
                                            )}
                                            {status === 'upcoming' && orderCount > 0 && (!meeting?.raffle || raffleCompleted) && (
                                                <Chip label="Confirmado" size="small" sx={{ bgcolor: '#e6f3f1', color: '#006a61', fontWeight: 500, fontSize: '0.7rem' }} />
                                            )}
                                            {status === 'upcoming' && orderCount > 0 && meeting?.raffle && !raffleCompleted && (
                                                <Chip label="Aguardando sorteio" size="small" sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2', fontWeight: 500, fontSize: '0.7rem' }} />
                                            )}
                                            {status === 'upcoming' && orderCount === 0 && (
                                                <Chip label="Disponível" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 500, fontSize: '0.7rem' }} />
                                            )}
                                            </Stack>
                                        </Stack>

                                        {meeting?.raffle && !raffleCompleted && item.raffle_at && (
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, minWidth: 96, flexShrink: 0 }}>
                                                    Sorteio até
                                                </Typography>
                                                <Typography variant="body2" color="#7b1fa2">
                                                    {formatarMomento(item.raffle_at)}
                                                </Typography>
                                            </Stack>
                                        )}

                                        {buyer ? (
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, minWidth: 96, flexShrink: 0 }}>
                                                    Comprado por
                                                </Typography>
                                                <Avatar src={buyer.profileImage} sx={{ width: 36, height: 36 }}>
                                                    {buyer.name?.[0]}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight={500}>{buyer.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {meeting?.raffle ? 'Sorteado' : 'Adquiriu este horário'}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        ) : meeting?.raffle && orderCount > 0 ? (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CasinoIcon sx={{ fontSize: 18, color: '#7b1fa2' }} />
                                                <Typography variant="body2" color="#7b1fa2" fontWeight={500}>
                                                    Sorteio nao realizado ({orderCount} participante{orderCount > 1 ? 's' : ''})
                                                </Typography>
                                            </Stack>
                                        ) : (
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <PersonOutline sx={{ fontSize: 18, color: '#ccc' }} />
                                                <Typography variant="body2" color="text.secondary">
                                                    Aguardando conviva
                                                </Typography>
                                            </Stack>
                                        )}

                                        {/* Termino fica de fora: o encontro ainda nao aconteceu */}
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, minWidth: 96, flexShrink: 0 }}>
                                                Início
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatarMomento(item.datetime) || '—'}
                                            </Typography>
                                        </Stack>

                                        {buyer && <DetalhesConviva buyer={buyer} />}

                                        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                                            {/* Canal interno (Termos 1.11): tirado o contato pessoal
                                                da tela, e por aqui que o anfitriao fala com quem
                                                comprou o horario */}
                                            {buyer && (chosenOrder?.id || orders[0]?.id) && (
                                                <BotaoConversar
                                                    orderId={chosenOrder?.id || orders[0]?.id}
                                                    label={`Conversar com ${buyer.name?.split(' ')[0] || 'o conviva'}`}
                                                />
                                            )}
                                            {meeting?.visible_public && (
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    startIcon={<ContentCopy sx={{ fontSize: 14 }} />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const url = `${window.location.origin}/lista/ao-vivo/${item.id}`;
                                                        navigator.clipboard.writeText(url);
                                                        enqueueSnackbar('Link de espectador copiado!', { variant: 'success' });
                                                    }}
                                                    sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#666' }}
                                                >
                                                    Copiar link espectador
                                                </Button>
                                            )}
                                            {meeting?.raffle && orderCount > 0 && (
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    startIcon={<PeopleOutline sx={{ fontSize: 16 }} />}
                                                    onClick={() => setParticipantsDialog({ orders, chosenOrder })}
                                                    sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#666' }}
                                                >
                                                    Ver participantes
                                                </Button>
                                            )}
                                            {meeting?.raffle && orderCount > 0 && !raffleCompleted && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={async () => {
                                                        try {
                                                            const { data: res } = await server.get(`/profile/schedule/raffle/${meeting.id}`);
                                                            const slot = (res.data || []).find(s => s.meeting_scheduled_id === item.id);
                                                            if (!slot?.participants?.length) {
                                                                enqueueSnackbar('Nenhum participante para sortear', { variant: 'info' });
                                                                return;
                                                            }
                                                            setRaffleData({
                                                                participants: slot.participants,
                                                                scheduleData: {
                                                                    meeting_id: meeting.id,
                                                                    datetime: item.datetime,
                                                                    meeting_scheduled_id: item.id,
                                                                },
                                                            });
                                                            setRaffleOpen(true);
                                                        } catch (err) {
                                                            enqueueSnackbar(err?.response?.data?.message || 'Erro ao carregar participantes', { variant: 'error' });
                                                        }
                                                    }}
                                                    disabled={isAfter(getServerNow(), new Date(new Date(item.datetime).getTime() + 3600000))}
                                                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                                                >
                                                    Sortear
                                                </Button>
                                            )}
                                            {orderCount === 0 && (
                                                <LoadingButton
                                                    size="small"
                                                    loading={deleting}
                                                    onClick={() => removeSchedule(item.id)}
                                                    startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
                                                    sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#999' }}
                                                >
                                                    Remover
                                                </LoadingButton>
                                            )}
                                            {orderCount > 0 && (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    disabled={status === 'upcoming'}
                                                    onClick={() => navigation(`/encontros/${item.Order[0]?.id}/chamada`)}
                                                    startIcon={<VideoCall sx={{ fontSize: 16 }} />}
                                                    sx={{
                                                        textTransform: 'none',
                                                        fontSize: '0.75rem',
                                                        bgcolor: '#006a61',
                                                        '&:hover': { bgcolor: '#005a52' },
                                                    }}
                                                >
                                                    Entrar
                                                </Button>
                                            )}
                                        </Stack>
                                    </Stack>
                                </SectionCard>
                            );
                        })}
                    </Stack>
                </>
            )}

            {/* Encerrados */}
            {ended.length > 0 && (
                <>
                    <Typography variant="subtitle2" fontWeight={600} color="text.secondary" sx={{ alignSelf: 'flex-start', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.75rem', mt: 1 }}>
                        Encerrados ({ended.length})
                    </Typography>
                    <Stack spacing={1.5} sx={{ width: '100%' }}>
                        {ended.map(item => {
                            const orderCount = Array.isArray(item.Order) ? item.Order.length : 0;
                            const buyer = item.Order?.[0]?.user;
                            const dt = new Date(item.datetime);
                            const date = dt.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
                            const time = dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

                            return (
                                <SectionCard key={item.id} sx={{ borderLeft: '4px solid #ddd', opacity: 0.7 }}>
                                    <Stack spacing={1}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CalendarToday sx={{ fontSize: 16, color: '#bbb' }} />
                                                <Typography variant="subtitle2" fontWeight={500} color="text.secondary">
                                                    {date}
                                                </Typography>
                                                <AccessTime sx={{ fontSize: 16, color: '#bbb' }} />
                                                <Typography variant="subtitle2" fontWeight={500} color="text.secondary">
                                                    {time}
                                                </Typography>
                                            </Stack>
                                            {item.canceled_at ? (
                                                <Chip label="Cancelado" size="small" sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 600, fontSize: '0.7rem' }} />
                                            ) : (
                                                <Chip label="Encerrado" size="small" sx={{ bgcolor: '#f5f5f5', color: '#999', fontSize: '0.7rem' }} />
                                            )}
                                        </Stack>

                                        {/* Motivo so existe quando o anfitriao escreveu um */}
                                        {item.cancel_reason && (
                                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, minWidth: 96, flexShrink: 0 }}>
                                                    Motivo
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                    {item.cancel_reason}
                                                </Typography>
                                            </Stack>
                                        )}
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, minWidth: 96, flexShrink: 0 }}>
                                                Comprado por
                                            </Typography>
                                            {orderCount > 0 && buyer ? (
                                                <>
                                                    <Avatar src={buyer.profileImage} sx={{ width: 24, height: 24, opacity: 0.7 }}>
                                                        {buyer.name?.[0]}
                                                    </Avatar>
                                                    <Typography variant="body2" color="text.secondary">{buyer.name}</Typography>
                                                </>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">Ninguém comprou</Typography>
                                            )}
                                        </Stack>

                                        {[
                                            ['Início', formatarMomento(item.datetime)],
                                            // finished_at so existe quando o encontro foi encerrado de
                                            // fato; senao vale o fim previsto, uma hora depois
                                            ['Término', formatarMomento(
                                                item.finished_at ||
                                                (item.datetime ? new Date(new Date(item.datetime).getTime() + 60 * 60 * 1000) : null)
                                            )],
                                        ].map(([rotulo, valor]) => (
                                            <Stack key={rotulo} direction="row" spacing={1.5} alignItems="center">
                                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, minWidth: 96, flexShrink: 0 }}>
                                                    {rotulo}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">{valor || '—'}</Typography>
                                            </Stack>
                                        ))}

                                        {orderCount > 0 && buyer && <DetalhesConviva buyer={buyer} />}

                                        {/* A conversa continua aberta por 30 dias depois do encontro:
                                            e nesse prazo que aparece reclamacao sobre o que foi
                                            entregue, e ela precisa acontecer dentro da Plataforma */}
                                        {orderCount > 0 && buyer && item.Order?.[0]?.id && (
                                            <Stack direction="row" justifyContent="flex-end">
                                                <BotaoConversar orderId={item.Order[0].id} />
                                            </Stack>
                                        )}
                                    </Stack>
                                </SectionCard>
                            );
                        })}
                    </Stack>
                </>
            )}

            {allSchedules.length === 0 && (
                <Alert severity="info" sx={{ width: '100%', borderRadius: '12px' }}>
                    Nenhum horário designado para este encontro
                </Alert>
            )}

            {/* Roleta de Sorteio */}
            {raffleData && (
                <RaffleWheel
                    open={raffleOpen}
                    onClose={() => { setRaffleOpen(false); setRaffleData(null); }}
                    participants={raffleData.participants}
                    scheduleData={raffleData.scheduleData}
                    mutate={mutate}
                />
            )}

            {/* Prazo do sorteio automatico */}
            <RaffleSettings
                open={Boolean(raffleSettings)}
                schedule={raffleSettings}
                onClose={() => setRaffleSettings(null)}
                onSaved={mutate}
            />

            {/* Dialog de Participantes */}
            <Dialog
                open={Boolean(participantsDialog)}
                onClose={() => setParticipantsDialog(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ fontWeight: 600, pr: 5 }}>
                    Participantes do sorteio
                    <IconButton onClick={() => setParticipantsDialog(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {participantsDialog?.chosenOrder && (
                        <Box sx={{ bgcolor: '#e8f5e9', px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <EmojiEvents sx={{ fontSize: 18, color: '#2e7d32' }} />
                            <Typography variant="body2" fontWeight={600} color="#2e7d32">
                                Sorteio realizado
                            </Typography>
                        </Box>
                    )}
                    {!participantsDialog?.chosenOrder && (
                        <Box sx={{ bgcolor: '#f3e5f5', px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CasinoIcon sx={{ fontSize: 18, color: '#7b1fa2' }} />
                            <Typography variant="body2" fontWeight={600} color="#7b1fa2">
                                Sorteio ainda não realizado
                            </Typography>
                        </Box>
                    )}
                    <List sx={{ pt: 0 }}>
                        {participantsDialog?.orders
                            ?.slice()
                            .sort((a, b) => (b.chosen ? 1 : 0) - (a.chosen ? 1 : 0))
                            .map((order) => (
                            <ListItem
                                key={order.id}
                                sx={{
                                    bgcolor: order.chosen ? '#e8f5e9' : 'transparent',
                                    borderLeft: order.chosen ? '4px solid #2e7d32' : '4px solid transparent',
                                }}
                            >
                                <ListItemAvatar>
                                    <Avatar
                                        src={order.user?.profileImage}
                                        sx={{
                                            border: order.chosen ? '2px solid #2e7d32' : '2px solid #e0e0e0',
                                        }}
                                    >
                                        {order.user?.name?.[0]}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Typography variant="body2" fontWeight={order.chosen ? 700 : 400}>
                                                {order.user?.name}
                                            </Typography>
                                            {order.chosen && (
                                                <Chip
                                                    icon={<EmojiEvents sx={{ fontSize: 14 }} />}
                                                    label="Vencedor"
                                                    size="small"
                                                    sx={{ bgcolor: '#2e7d32', color: 'white', fontWeight: 600, fontSize: '0.65rem', height: 20, '& .MuiChip-icon': { color: 'white' } }}
                                                />
                                            )}
                                        </Stack>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                    <Box sx={{ px: 3, pb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            {participantsDialog?.orders?.length} participante{participantsDialog?.orders?.length > 1 ? 's' : ''} no total
                        </Typography>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
};
